import {
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfWeek,
  format,
  startOfDay,
  startOfMonth,
  startOfWeek,
  startOfYear,
} from 'date-fns';
import { tr } from 'date-fns/locale';
import type { SupabaseClient } from '@supabase/supabase-js';
import { calculateForumScore } from './forumScore';

export type ForumStatSnapshotRow = {
  topics_count: number;
  posts_count: number;
  members_count: number;
  recorded_at: string;
};

export type HistoryMetric = 'topics' | 'posts' | 'members' | 'score';

export type HistoryGranularity = 'daily' | 'weekly' | 'monthly' | 'yearly';

export const HISTORY_METRICS: HistoryMetric[] = ['topics', 'posts', 'members', 'score'];

export const HISTORY_METRIC_COLORS: Record<HistoryMetric, string> = {
  topics: '#7c3aed',
  posts: '#059669',
  members: '#2563eb',
  score: '#d97706',
};

export type HistoryChartPoint = {
  recordedAt: string;
  label: string;
  topics: number;
  posts: number;
  members: number;
  score: number;
  /** true: o dönemde gerçek bir güncelleme kaydı var; false: önceki değer taşındı (düz çizgi). */
  isActual: boolean;
};

export type ForumForHistory = {
  id: string;
  topics_count: number;
  posts_count: number;
  members_count: number;
  founded_year: number;
  previous_month_topics_count?: number | null;
  previous_month_posts_count?: number | null;
  previous_month_members_count?: number | null;
  updated_at?: string | null;
  created_at?: string | null;
};

const METRIC_KEYS: Record<HistoryMetric, keyof Pick<HistoryChartPoint, 'topics' | 'posts' | 'members' | 'score'>> = {
  topics: 'topics',
  posts: 'posts',
  members: 'members',
  score: 'score',
};

export const HISTORY_METRIC_LABELS: Record<HistoryMetric, string> = {
  topics: 'Konu artışı',
  posts: 'İleti artışı',
  members: 'Üye artışı',
  score: 'Skor değişimi',
};

/** Tooltip / eksen için işaretli sayı (+1.234 / -5). */
export function formatHistoryDelta(value: number): string {
  const abs = Math.abs(value);
  const body =
    abs >= 1_000_000
      ? `${(abs / 1_000_000).toFixed(1)}M`
      : abs >= 1000
        ? `${(abs / 1000).toFixed(1)}K`
        : abs.toLocaleString('tr-TR');
  if (value > 0) return `+${body}`;
  if (value < 0) return `−${body}`;
  return '0';
}

/**
 * Toplam sayı serisini dönemsel artışa çevirir (nokta[i] − nokta[i−1]).
 * İlk nokta atılır. Forward-fill dönemlerde artış 0 olur (düz çizgi).
 */
export function toPeriodDeltaChartPoints(points: HistoryChartPoint[]): HistoryChartPoint[] {
  if (points.length < 2) return [];

  const out: HistoryChartPoint[] = [];
  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const cur = points[i];
    out.push({
      recordedAt: cur.recordedAt,
      label: cur.label,
      topics: cur.topics - prev.topics,
      posts: cur.posts - prev.posts,
      members: cur.members - prev.members,
      score: Math.round((cur.score - prev.score) * 10) / 10,
      isActual: cur.isActual,
    });
  }
  return out;
}

function formatChartLabel(iso: string): string {
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return format(d, 'd MMM yyyy', { locale: tr });
  } catch {
    return iso;
  }
}

function toChartPoint(
  topics: number,
  posts: number,
  members: number,
  foundedYear: number,
  recordedAt: string
): HistoryChartPoint {
  return {
    recordedAt,
    label: formatChartLabel(recordedAt),
    topics,
    posts,
    members,
    score: calculateForumScore(topics, posts, members, foundedYear),
    isActual: true,
  };
}

/** Yalnızca forum_stat_snapshots tablosundaki gerçek kayıtlar — sentetik tarih/önceki ay eklenmez. */
export function buildHistoryChartPoints(
  snapshots: ForumStatSnapshotRow[],
  forum: ForumForHistory
): HistoryChartPoint[] {
  return snapshots
    .map((s) =>
      toChartPoint(s.topics_count, s.posts_count, s.members_count, forum.founded_year, s.recorded_at)
    )
    .sort((a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime());
}

export function getHistoryMetricValue(point: HistoryChartPoint, metric: HistoryMetric): number {
  return point[METRIC_KEYS[metric]];
}

function historyBucketKey(iso: string, granularity: HistoryGranularity): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (granularity === 'daily') return format(d, 'yyyy-MM-dd');
  if (granularity === 'weekly') {
    return format(startOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
  }
  if (granularity === 'yearly') return format(d, 'yyyy');
  return format(d, 'yyyy-MM');
}

function formatGranularityLabel(iso: string, granularity: HistoryGranularity): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  if (granularity === 'daily') return formatChartLabel(iso);
  if (granularity === 'weekly') {
    const start = startOfWeek(d, { weekStartsOn: 1 });
    const end = endOfWeek(d, { weekStartsOn: 1 });
    return `${format(start, 'd MMM', { locale: tr })} – ${format(end, 'd MMM yyyy', { locale: tr })}`;
  }
  if (granularity === 'yearly') return format(d, 'yyyy');
  return format(d, 'MMM yyyy', { locale: tr });
}

/** Dönem başı (gün/hafta/ay/yıl) — sürekli seri üretmek için. */
function bucketStartDate(date: Date, granularity: HistoryGranularity): Date {
  if (granularity === 'daily') return startOfDay(date);
  if (granularity === 'weekly') return startOfWeek(date, { weekStartsOn: 1 });
  if (granularity === 'yearly') return startOfYear(date);
  return startOfMonth(date);
}

/** Bir sonraki döneme ilerle. */
function advanceBucket(date: Date, granularity: HistoryGranularity): Date {
  if (granularity === 'daily') return addDays(date, 1);
  if (granularity === 'weekly') return addWeeks(date, 1);
  if (granularity === 'yearly') return addYears(date, 1);
  return addMonths(date, 1);
}

/** Aynı gün/hafta/ay içindeki kayıtları birleştirir; dönem sonundaki (en güncel) değeri alır. */
export function aggregateHistoryChartPoints(
  points: HistoryChartPoint[],
  granularity: HistoryGranularity
): HistoryChartPoint[] {
  if (points.length === 0) return [];

  const buckets = new Map<string, HistoryChartPoint>();

  for (const p of points) {
    const key = historyBucketKey(p.recordedAt, granularity);
    const existing = buckets.get(key);
    if (!existing || new Date(p.recordedAt).getTime() > new Date(existing.recordedAt).getTime()) {
      buckets.set(key, {
        ...p,
        label: formatGranularityLabel(p.recordedAt, granularity),
      });
    }
  }

  return Array.from(buckets.values()).sort(
    (a, b) => new Date(a.recordedAt).getTime() - new Date(b.recordedAt).getTime()
  );
}

const MAX_CONTINUOUS_BUCKETS = 4000;

/**
 * Kesintisiz zaman serisi: ilk kayıttan bugüne kadar her dönem için bir nokta üretir.
 * Güncelleme olmayan dönemlerde son bilinen değer taşınır (düz çizgi). isActual ile ayrılır.
 */
export function buildContinuousChartPoints(
  points: HistoryChartPoint[],
  granularity: HistoryGranularity,
  now: Date = new Date()
): HistoryChartPoint[] {
  const aggregated = aggregateHistoryChartPoints(points, granularity);
  if (aggregated.length === 0) return [];

  const byKey = new Map<string, HistoryChartPoint>();
  for (const p of aggregated) {
    byKey.set(historyBucketKey(p.recordedAt, granularity), p);
  }

  const firstDate = bucketStartDate(new Date(aggregated[0].recordedAt), granularity);
  const lastActualDate = new Date(aggregated[aggregated.length - 1].recordedAt);
  const endAnchor = now.getTime() > lastActualDate.getTime() ? now : lastActualDate;
  const endDate = bucketStartDate(endAnchor, granularity);

  const out: HistoryChartPoint[] = [];
  let cursor = firstDate;
  let last: HistoryChartPoint | null = null;
  let guard = 0;

  while (cursor.getTime() <= endDate.getTime() && guard < MAX_CONTINUOUS_BUCKETS) {
    guard += 1;
    const cursorIso = cursor.toISOString();
    const key = historyBucketKey(cursorIso, granularity);
    const actual = byKey.get(key);
    const label = formatGranularityLabel(cursorIso, granularity);

    if (actual) {
      last = actual;
      out.push({ ...actual, label, isActual: true });
    } else if (last) {
      out.push({ ...last, recordedAt: cursorIso, label, isActual: false });
    }

    cursor = advanceBucket(cursor, granularity);
  }

  return out;
}

/** Tek grafik modu: her metrik, kendi |maks| değerine göre göreli ölçek (−100…100 civarı). */
export function buildCombinedChartRows(
  points: HistoryChartPoint[],
  visibleMetrics: HistoryMetric[]
): Array<Record<string, string | number>> {
  if (points.length === 0) return [];

  const baselines: Partial<Record<HistoryMetric, number>> = {};
  for (const m of visibleMetrics) {
    let maxAbs = 0;
    for (const p of points) {
      maxAbs = Math.max(maxAbs, Math.abs(p[METRIC_KEYS[m]]));
    }
    baselines[m] = maxAbs > 0 ? maxAbs : 1;
  }

  return points.map((p) => {
    const row: Record<string, string | number> = {
      label: p.label,
      recordedAt: p.recordedAt,
      isActual: p.isActual ? 1 : 0,
    };
    for (const m of HISTORY_METRICS) {
      const raw = p[METRIC_KEYS[m]];
      row[`raw_${m}`] = raw;
      if (visibleMetrics.includes(m)) {
        const base = baselines[m] ?? 1;
        row[m] = Math.round((raw / base) * 1000) / 10;
      }
    }
    return row;
  });
}

export async function fetchForumStatSnapshots(
  supabase: SupabaseClient,
  forumId: string
): Promise<ForumStatSnapshotRow[]> {
  const { data, error } = await supabase
    .from('forum_stat_snapshots')
    .select('topics_count, posts_count, members_count, recorded_at')
    .eq('forum_id', forumId)
    .order('recorded_at', { ascending: true });

  if (error) {
    console.warn('forum_stat_snapshots fetch:', error.message);
    return [];
  }
  return data ?? [];
}

/** Sayılar değiştiyse veya son kayıttan ≥1 saat sonra tekrar güncellendiyse geçmiş satırı ekler. */
export async function recordForumStatSnapshot(
  supabase: SupabaseClient,
  forum: {
    id: string;
    topics_count: number;
    posts_count: number;
    members_count: number;
  },
  recordedAt?: string,
  options?: { force?: boolean }
): Promise<void> {
  const at = recordedAt ?? new Date().toISOString();

  if (!options?.force) {
    const { data: last } = await supabase
      .from('forum_stat_snapshots')
      .select('topics_count, posts_count, members_count, recorded_at')
      .eq('forum_id', forum.id)
      .order('recorded_at', { ascending: false })
      .limit(1)
      .maybeSingle();

    const sameValues =
      last &&
      last.topics_count === forum.topics_count &&
      last.posts_count === forum.posts_count &&
      last.members_count === forum.members_count;

    if (sameValues) {
      const lastAt = last.recorded_at ? new Date(last.recorded_at).getTime() : 0;
      const nextAt = new Date(at).getTime();
      if (!Number.isFinite(lastAt) || !Number.isFinite(nextAt) || nextAt - lastAt < 60 * 60 * 1000) {
        return;
      }
    }
  }

  const { error } = await supabase.from('forum_stat_snapshots').insert({
    forum_id: forum.id,
    topics_count: forum.topics_count,
    posts_count: forum.posts_count,
    members_count: forum.members_count,
    recorded_at: at,
  });

  if (error) {
    console.error('forum_stat_snapshots insert failed:', error);
  }
}
